import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n";
import type { CronJob, CronSchedule } from "../types/api";

function describeSchedule(s: CronSchedule, t: (k: string) => string): string {
  if (s.kind === "interval") return `${t("cron.every")} ${s.minutes} ${t("cron.minutes")}`;
  const hh = String(s.hour).padStart(2, "0");
  const mm = String(s.minute).padStart(2, "0");
  return `${t("cron.dailyAt")} ${hh}:${mm}`;
}

export function CronModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { client, account } = useAuth();
  const { t, lang } = useI18n();
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState<"interval" | "daily">("daily");
  const [minutes, setMinutes] = useState("60");
  const [time, setTime] = useState("08:00");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!client || !account) return;
    try {
      const { jobs } = await client.listCronJobs(account.id);
      setJobs(jobs);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed"));
    } finally {
      setLoading(false);
    }
  }, [client, account, t]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  if (!open) return null;

  function buildSchedule(): CronSchedule | null {
    if (kind === "interval") {
      const m = Number(minutes);
      if (!Number.isInteger(m) || m < 1) return null;
      return { kind: "interval", minutes: m };
    }
    const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return null;
    return { kind: "daily", hour, minute };
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!client || !account) return;
    const schedule = buildSchedule();
    if (!schedule || !name.trim() || !prompt.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await client.createCronJob(account.id, { name: name.trim(), prompt: prompt.trim(), schedule });
      setName("");
      setPrompt("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(job: CronJob) {
    if (!client || !account) return;
    try {
      await client.setCronJobEnabled(account.id, job.id, !job.enabled);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed"));
    }
  }

  async function remove(job: CronJob) {
    if (!client || !account) return;
    try {
      await client.deleteCronJob(account.id, job.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed"));
    }
  }

  return (
    <div className="modal-root" role="dialog" aria-modal="true" aria-label={t("cron.title")}>
      <div className="modal-scrim" onClick={onClose} />
      <div className="modal-card">
        <div className="modal-head">
          <span className="modal-title">{t("cron.title")}</span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {error ? <div className="error-banner">{error}</div> : null}

          {loading ? (
            <div className="center" style={{ height: 60 }}>
              <div className="spinner" />
            </div>
          ) : jobs.length === 0 ? (
            <p className="muted settings-hint">{t("cron.empty")}</p>
          ) : (
            <div className="cron-list">
              {jobs.map((job) => (
                <div key={job.id} className={`cron-row ${job.enabled ? "" : "disabled"}`}>
                  <div className="cron-row-main">
                    <span className="cron-name" title={job.prompt}>
                      {job.name}
                    </span>
                    <span className="cron-meta">
                      {describeSchedule(job.schedule, t)}
                      {job.enabled
                        ? ` · ${t("cron.next")} ${new Date(job.nextRunAt).toLocaleString(lang)}`
                        : ` · ${t("cron.paused")}`}
                    </span>
                  </div>
                  <div className="cron-row-actions">
                    {/* U+FE0E forces text (monochrome) presentation so these match the header line icons. */}
                    <button type="button" className="icon-btn" onClick={() => void toggle(job)} title={job.enabled ? t("cron.pause") : t("cron.resume")}>
                      {job.enabled ? "⏸︎" : "▶︎"}
                    </button>
                    <button type="button" className="icon-btn" onClick={() => void remove(job)} title={t("cron.delete")}>
                      {"🗑︎"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="cron-create">
            <p className="modal-title cron-create-title">{t("cron.createTitle")}</p>
            <label className="label" htmlFor="cron-name">
              {t("cron.name")}
            </label>
            <input id="cron-name" className="input" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />

            <label className="label" htmlFor="cron-prompt">
              {t("cron.prompt")}
            </label>
            <textarea
              id="cron-prompt"
              className="input cron-prompt"
              rows={2}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={busy}
            />

            <label className="label" htmlFor="cron-kind">
              {t("cron.schedule")}
            </label>
            <div className="cron-schedule-row">
              <select id="cron-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as "interval" | "daily")} disabled={busy}>
                <option value="daily">{t("cron.kindDaily")}</option>
                <option value="interval">{t("cron.kindInterval")}</option>
              </select>
              {kind === "interval" ? (
                <input
                  className="input"
                  type="number"
                  min={1}
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  aria-label={t("cron.minutes")}
                  disabled={busy}
                />
              ) : (
                <input
                  className="input"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  aria-label={t("cron.dailyAt")}
                  disabled={busy}
                />
              )}
            </div>

            <button type="submit" className="btn-primary settings-save" disabled={busy || !name.trim() || !prompt.trim() || !buildSchedule()}>
              {busy ? <span className="spinner inline" /> : t("cron.create")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
